import {
  apiGet,
  apiPost,
  apiPut,
  apiPatch,
  apiDelete,
  apiGetRaw,
  apiPostFormData,
} from "@/lib/api/_apiShim";
import type {
  Offering,
  Lane,
  Treatment,
  Appointment,
  AppointmentRequest,
  AppointmentStatus,
  AppointmentGuest,
  OperationHistory,
  BatchJob,
  RecurringPattern,
  SlotSuggestion,
  OfferingStats,
  DailyBreakdown,
  GuestAppointmentsResponse,
  LinkedAppointmentGroup,
  OfferingTemplate,
  OfferingTicketRule,
  TreatmentOccupancy,
  WaitlistEntry,
} from "../types";
import type { AppointmentPreviewRow } from "@/components/A4Builder/appointment-record";
import { withAiContentTool } from "@/lib/ai/aiContentQuery";

const BASE = "/api/v1/slotting";

function offeringPath(eventId: string, offeringId: string) {
  return `${BASE}/${eventId}/offering/${offeringId}`;
}

async function authHeaders(): Promise<Record<string, string>> {
  return { "Cache-Control": "no-store" };
}

// --- Email Templates ---

export async function getEmailTemplates(
  eventId: string,
): Promise<{ emailTemplateId: string; templateName: string }[]> {
  const headers = await authHeaders();
  try {
    return await apiGet<{ emailTemplateId: string; templateName: string }[]>(
      `/api/v1/email-template/event/${eventId}?fields=emailTemplateId,templateName`,
      headers,
      true,
    );
  } catch (e) {
    console.error('getEmailTemplates failed:', e);
    return [];
  }
}

// --- Offering ---

export async function getOfferings(
  eventId: string,
  /** Ride the admin's own auth instead of an active preview bearer. The
   *  role-template scope pickers need the FULL catalogue to widen a restriction;
   *  without this the preview bearer filters the list to the role's already-visible
   *  subset. See client.ts `X-Preview-Bypass`. */
  bypassPreview = false,
): Promise<Offering[]> {
  const headers = await authHeaders();
  if (bypassPreview) headers["X-Preview-Bypass"] = "1";
  return apiGet<Offering[]>(`${BASE}/${eventId}/offering`, headers, true);
}

/**
 * Every treatment of the event (across all offerings) — options for the guest-list
 * "Behandlung" filter (Field.optionSource "treatments"). Best-effort: returns [] on
 * error so the picker degrades to empty rather than breaking the filter UI.
 */
export async function getEventTreatments(
  eventId: string,
  /** See {@link getOfferings} — bypass an active preview bearer for the full list. */
  bypassPreview = false,
): Promise<Treatment[]> {
  const headers = await authHeaders();
  if (bypassPreview) headers["X-Preview-Bypass"] = "1";
  try {
    return await apiGet<Treatment[]>(`${BASE}/${eventId}/treatments`, headers, true);
  } catch {
    return [];
  }
}

/**
 * Thrown by {@link getTreatmentOccupancy} when the event lacks the SLOTTING
 * plugin (403) — a DISTINGUISHABLE signal so callers can degrade (hide the
 * occupancy badge) instead of treating it as a fetch failure.
 */
export class SlottingUnavailableError extends Error {
  constructor() {
    super("pluginDisabled");
    this.name = "SlottingUnavailableError";
  }
}

/**
 * Strict twin of {@link getEventTreatments} for callers that must tell a
 * COLD or FAILED catalogue from an EMPTY one — the programme lanes'
 * `offerWindowMoved` guard (PB-POM b3). With the swallow above a network
 * failure resolved `[]`, the query's `data` was present, and a bound block's
 * length change passed as if the container held no offer that could flip.
 *
 * ONLY the backend's own `pluginDisabled` code resolves `[]` (no SLOTTING
 * plugin = legitimately no offers). NOT "any 403": this GET is not
 * plugin-gated on reads (`@RequiresPlugin` soft-disable — plugin off answers
 * 200 + rows), so a 403 here is `insufficientCapability` — a role that may
 * not READ the rows. Unreadable rows are UNKNOWN rows, and an unknown
 * catalogue must keep `data === undefined` so the guard refuses the length
 * change instead of treating the container as empty (audit r1 S1/F2).
 * Everything else (401, 5xx, network) rejects for the same reason.
 */
export async function getEventTreatmentsStrict(
  eventId: string,
): Promise<Treatment[]> {
  const headers = await authHeaders();
  try {
    return await apiGet<Treatment[]>(`${BASE}/${eventId}/treatments`, headers, true);
  } catch (e) {
    // `rethrowApi` puts the backend errorCode on `message`.
    if ((e as Error | undefined)?.message === "pluginDisabled") return [];
    throw e;
  }
}

/**
 * Live occupancy per treatment (active appointments + booked guests vs
 * capacity) — feeds the programme timeline's OFFERING badge. THROWS
 * {@link SlottingUnavailableError} on 403 (no SLOTTING plugin); every other
 * failure rethrows as-is (callers must surface it, not silently blank).
 */
export async function getTreatmentOccupancy(
  eventId: string,
): Promise<TreatmentOccupancy[]> {
  const headers = await authHeaders();
  try {
    return await apiGet<TreatmentOccupancy[]>(
      `${BASE}/${eventId}/treatments/occupancy`,
      headers,
      true,
    );
  } catch (e) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status === 403) throw new SlottingUnavailableError();
    throw e;
  }
}

/**
 * Every lane of the event (across all offerings) — options for the guest-list "Lane"
 * filter (Field.optionSource "lanes"). Best-effort: returns [] on error.
 */
export async function getEventLanes(
  eventId: string,
  /** See {@link getOfferings} — bypass an active preview bearer for the full list. */
  bypassPreview = false,
): Promise<Lane[]> {
  const headers = await authHeaders();
  if (bypassPreview) headers["X-Preview-Bypass"] = "1";
  try {
    return await apiGet<Lane[]>(`${BASE}/${eventId}/lanes`, headers, true);
  } catch {
    return [];
  }
}

export async function getOffering(
  eventId: string,
  offeringId: string,
): Promise<Offering> {
  const headers = await authHeaders();
  return apiGet<Offering>(`${offeringPath(eventId, offeringId)}`, headers, true);
}

export async function createOffering(
  eventId: string,
  offering: Partial<Offering>,
  /**
   * Art. 50 Abs. 2 KI-VO: the AI lane that wrote this offering's name /
   * description (`slotting` = the create wizard's copilot, a Setup Hub
   * `proposalAiTool`). The body IS the entity, so the tool travels as
   * `?aiContentTool=`; the server stamps the event's guest-content mark.
   */
  aiContentTool?: string,
): Promise<Offering> {
  const headers = await authHeaders();
  return apiPost<Offering>(
    withAiContentTool(`${BASE}/${eventId}/offering`, aiContentTool),
    offering,
    headers,
    true,
  );
}

// --- Offering Update ---

export async function updateOffering(
  eventId: string,
  offeringId: string,
  data: Partial<Offering>,
): Promise<Offering> {
  const headers = await authHeaders();
  return apiPut<Offering>(offeringPath(eventId, offeringId), data, headers, true);
}

export async function deleteOffering(
  eventId: string,
  offeringId: string,
): Promise<void> {
  const headers = await authHeaders();
  await apiDelete<string>(
    `${offeringPath(eventId, offeringId)}`,
    headers,
  );
}

export async function bulkDeleteOfferings(
  eventId: string,
  offeringIds: string[],
): Promise<void> {
  const headers = await authHeaders();
  await apiPost<void>(
    `${BASE}/${eventId}/offering/bulk-delete`,
    { offeringIds },
    headers,
    true,
  );
}

export async function bulkDuplicateOfferings(
  eventId: string,
  offeringIds: string[],
): Promise<Offering[]> {
  const headers = await authHeaders();
  return apiPost<Offering[]>(
    `${BASE}/${eventId}/offering/bulk-duplicate`,
    { offeringIds },
    headers,
    true,
  );
}

// --- Lanes ---

export async function getLanes(
  eventId: string,
  offeringId: string,
): Promise<Lane[]> {
  const headers = await authHeaders();
  return apiGet<Lane[]>(
    `${offeringPath(eventId, offeringId)}/lane`,
    headers,
    true,
  );
}

export async function upsertLanes(
  eventId: string,
  offeringId: string,
  lanes: Partial<Lane>[],
  /** Art. 50 KI-VO: the AI lane behind these lane names (`?aiContentTool=`). */
  aiContentTool?: string,
): Promise<Lane[]> {
  const headers = await authHeaders();
  return apiPost<Lane[]>(
    withAiContentTool(`${offeringPath(eventId, offeringId)}/lane`, aiContentTool),
    lanes,
    headers,
    true,
  );
}

export async function getLane(
  eventId: string,
  offeringId: string,
  laneId: string,
): Promise<Lane> {
  const headers = await authHeaders();
  return apiGet<Lane>(
    `${offeringPath(eventId, offeringId)}/lane/${laneId}`,
    headers,
    true,
  );
}

export async function deleteLane(
  eventId: string,
  offeringId: string,
  laneId: string,
): Promise<void> {
  const headers = await authHeaders();
  await apiDelete<string>(
    `${offeringPath(eventId, offeringId)}/lane/${laneId}`,
    headers,
  );
}

// --- Lane Availability ---

export async function updateLaneAvailability(
  eventId: string,
  offeringId: string,
  laneId: string,
  availability: import("../types").LaneAvailability[],
): Promise<import("../types").Lane> {
  const headers = await authHeaders();
  return apiPut<import("../types").Lane>(
    `${offeringPath(eventId, offeringId)}/lane/${laneId}/availability`,
    availability,
    headers,
    true,
  );
}

// --- Treatments ---

export async function getTreatments(
  eventId: string,
  offeringId: string,
): Promise<Treatment[]> {
  const headers = await authHeaders();
  return apiGet<Treatment[]>(
    `${offeringPath(eventId, offeringId)}/treatment`,
    headers,
    true,
  );
}

export async function getTreatment(
  eventId: string,
  offeringId: string,
  treatmentId: string,
): Promise<Treatment> {
  const headers = await authHeaders();
  return apiGet<Treatment>(
    `${offeringPath(eventId, offeringId)}/treatment/${treatmentId}`,
    headers,
    true,
  );
}

export async function upsertTreatments(
  eventId: string,
  offeringId: string,
  treatments: Partial<Treatment>[],
  /** Art. 50 KI-VO: the AI lane behind these treatment names (`?aiContentTool=`). */
  aiContentTool?: string,
): Promise<Treatment[]> {
  const headers = await authHeaders();
  return apiPost<Treatment[]>(
    withAiContentTool(`${offeringPath(eventId, offeringId)}/treatment`, aiContentTool),
    treatments,
    headers,
    true,
  );
}

export async function deleteTreatment(
  eventId: string,
  offeringId: string,
  treatmentId: string,
): Promise<void> {
  const headers = await authHeaders();
  await apiDelete<string>(
    `${offeringPath(eventId, offeringId)}/treatment/${treatmentId}`,
    headers,
  );
}

// --- Appointments ---

export async function getAppointments(
  eventId: string,
  offeringId: string,
  startDt: string,
  endDt: string,
): Promise<Appointment[]> {
  const headers = await authHeaders();
  return apiGet<Appointment[]>(
    `${offeringPath(eventId, offeringId)}/appointment?startDt=${encodeURIComponent(startDt)}&endDt=${encodeURIComponent(endDt)}`,
    headers,
    true,
  );
}

export async function createAppointment(
  eventId: string,
  offeringId: string,
  appointment: Partial<Appointment>,
): Promise<Appointment> {
  const headers = await authHeaders();
  return apiPost<Appointment>(
    `${offeringPath(eventId, offeringId)}/appointment`,
    appointment,
    headers,
    true,
  );
}

export async function updateAppointment(
  eventId: string,
  offeringId: string,
  appointmentId: string,
  appointment: Partial<Appointment>,
): Promise<Appointment> {
  const headers = await authHeaders();
  return apiPut<Appointment>(
    `${offeringPath(eventId, offeringId)}/appointment/${appointmentId}`,
    appointment,
    headers,
    true,
  );
}

export async function cancelAppointment(
  eventId: string,
  offeringId: string,
  appointmentId: string,
  reason: string,
): Promise<void> {
  const headers = await authHeaders();
  await apiDelete<string>(
    `${offeringPath(eventId, offeringId)}/appointment/${appointmentId}`,
    headers,
    { reason },
  );
}

export async function updateAppointmentStatus(
  eventId: string,
  offeringId: string,
  appointmentId: string,
  status: AppointmentStatus,
): Promise<Appointment> {
  const headers = await authHeaders();
  return apiPatch<Appointment>(
    `${offeringPath(eventId, offeringId)}/appointment/${appointmentId}/status`,
    { status },
    headers,
    true,
  );
}

// --- Appointment Guests ---

export async function addGuestsToAppointment(
  eventId: string,
  offeringId: string,
  appointmentId: string,
  guests: AppointmentGuest[],
): Promise<Appointment> {
  const headers = await authHeaders();
  return apiPost<Appointment>(
    `${offeringPath(eventId, offeringId)}/appointment/${appointmentId}/guests`,
    guests,
    headers,
    true,
  );
}

export async function removeGuestFromAppointment(
  eventId: string,
  offeringId: string,
  appointmentId: string,
  guestId: string,
): Promise<void> {
  const headers = await authHeaders();
  await apiDelete<string>(
    `${offeringPath(eventId, offeringId)}/appointment/${appointmentId}/guests/${guestId}`,
    headers,
  );
}

// --- Requests ---

export async function getRequests(
  eventId: string,
  offeringId: string,
  page: number,
  size: number,
  filters?: { treatmentId?: string; archived?: boolean; search?: string; filters?: string },
): Promise<{ content: AppointmentRequest[]; totalElements: number }> {
  const headers = await authHeaders();
  let url = `${offeringPath(eventId, offeringId)}/request?page=${page}&size=${size}`;
  if (filters?.treatmentId) url += `&treatmentId=${encodeURIComponent(filters.treatmentId)}`;
  if (filters?.archived !== undefined) url += `&archived=${filters.archived}`;
  if (filters?.search && filters.search.trim()) url += `&search=${encodeURIComponent(filters.search.trim())}`;
  if (filters?.filters && filters.filters.trim()) url += `&filters=${encodeURIComponent(filters.filters)}`;
  return apiGet<{ content: AppointmentRequest[]; totalElements: number }>(
    url,
    headers,
    true,
  );
}

/**
 * Authoritative pre-flight: the existing request for this guest in THIS offering,
 * or null. Backed by the same server lookup the create endpoint gates on, so it is
 * consistent with what create would reject — INCLUDING an archived request. Use it
 * (not the paginated store) to decide whether to route the user to the existing
 * request instead of letting them attempt a duplicate. 204 → null.
 */
export async function getRequestByGuestInOffering(
  eventId: string,
  offeringId: string,
  guestId: string,
): Promise<AppointmentRequest | null> {
  const headers = await authHeaders();
  const r = await apiGet<AppointmentRequest | "" | null>(
    `${offeringPath(eventId, offeringId)}/request/by-guest/${guestId}`,
    headers,
    true,
  );
  return r && typeof r === "object" ? r : null;
}

/**
 * Accurate active/archived request totals straight from the server, independent
 * of how many pages the sidebar has lazily loaded. `size=1` keeps the payload
 * tiny — we only read `totalElements`.
 */
export async function getRequestCounts(
  eventId: string,
  offeringId: string,
): Promise<{ active: number; archived: number }> {
  const [active, archived] = await Promise.all([
    getRequests(eventId, offeringId, 0, 1, { archived: false }),
    getRequests(eventId, offeringId, 0, 1, { archived: true }),
  ]);
  return { active: active.totalElements, archived: archived.totalElements };
}

export async function getRequest(
  eventId: string,
  offeringId: string,
  requestId: string,
): Promise<AppointmentRequest> {
  const headers = await authHeaders();
  return apiGet<AppointmentRequest>(
    `${offeringPath(eventId, offeringId)}/request/${requestId}`,
    headers,
    true,
  );
}

export async function createRequest(
  eventId: string,
  offeringId: string,
  request: {
    guestId: string;
    treatmentIds: string[];
    availability: { startDt: string; endDt: string }[];
    notes?: string;
  },
): Promise<AppointmentRequest> {
  const headers = await authHeaders();
  return apiPost<AppointmentRequest>(
    `${offeringPath(eventId, offeringId)}/request`,
    request,
    headers,
    true,
  );
}

export async function updateRequest(
  eventId: string,
  offeringId: string,
  requestId: string,
  data: {
    treatmentIds?: string[];
    availability?: { startDt: string; endDt: string }[];
    notes?: string;
  },
): Promise<AppointmentRequest> {
  const headers = await authHeaders();
  return apiPut<AppointmentRequest>(
    `${offeringPath(eventId, offeringId)}/request/${requestId}`,
    data,
    headers,
    true,
  );
}

export async function archiveRequest(
  eventId: string,
  offeringId: string,
  requestId: string,
): Promise<void> {
  const headers = await authHeaders();
  await apiDelete<string>(
    `${offeringPath(eventId, offeringId)}/request/${requestId}`,
    headers,
  );
}

export async function unarchiveRequest(
  eventId: string,
  offeringId: string,
  requestId: string,
): Promise<void> {
  const headers = await authHeaders();
  await apiPatch<string>(
    `${offeringPath(eventId, offeringId)}/request/${requestId}/unarchive`,
    {},
    headers,
  );
}

// --- Warning Dismissal ---

export async function dismissWarning(
  eventId: string,
  offeringId: string,
  appointmentId: string,
  warningType: string,
): Promise<Appointment> {
  const headers = await authHeaders();
  return apiPatch<Appointment>(
    `${offeringPath(eventId, offeringId)}/appointment/${appointmentId}/warnings/dismiss`,
    { type: warningType },
    headers,
    true,
  );
}

// --- Guest Search (for slotting) ---

export async function searchGuestsForSlotting(
  eventId: string,
  searchTerm: string,
): Promise<import("@/types/Guest").Guest[]> {
  // Family-aware search: returns a flat list ordered family-contiguous (main guest first, then its
  // companions), so the picker can indent companions and let staff book any of them. See
  // SlottingGuestService on the backend.
  const headers = await authHeaders();
  try {
    const res = await apiGet<{ guests: import("@/types/Guest").Guest[]; total: number }>(
      `${BASE}/${eventId}/guests/search?q=${encodeURIComponent(searchTerm)}&limit=20&offset=0`,
      headers,
      true,
    );
    return res.guests ?? [];
  } catch {
    return [];
  }
}

export async function getGuestById(
  eventId: string,
  guestId: string,
): Promise<import("@/types/Guest").Guest | null> {
  // Slotting-scoped, authz-aware endpoint (cap slotting:read) — a slotting-only
  // role has no guests:read, so the general /guests endpoint 403s. This returns
  // display fields only, honoring the role's guest scope.
  const headers = await authHeaders();
  try {
    return await apiGet<import("@/types/Guest").Guest>(
      `${BASE}/${eventId}/guest/${guestId}`,
      headers,
      true,
    );
  } catch (e) {
    console.error('getGuestById failed:', e);
    return null;
  }
}

export async function getGuestsByIds(
  eventId: string,
  guestIds: string[],
  /** Optional projection widening beyond the name default (status-dot rules
   *  need e.g. checkedIn / additionalFields). BE sanitizes fail-closed against
   *  the guest condition allowlist and still applies the caller's field scope. */
  fields?: string[],
): Promise<import("@/types/Guest").Guest[]> {
  if (guestIds.length === 0) return [];
  const headers = await authHeaders();
  const query =
    fields && fields.length > 0
      ? `?fields=${encodeURIComponent(fields.join(","))}`
      : "";
  try {
    return await apiPost<import("@/types/Guest").Guest[]>(
      `${BASE}/${eventId}/guests/by-ids${query}`,
      guestIds,
      headers,
      true,
    );
  } catch (e) {
    console.error('getGuestsByIds failed:', e);
    return [];
  }
}

// --- Undo Stack ---

export async function getUndoStack(
  eventId: string,
  offeringId: string,
): Promise<OperationHistory[]> {
  const headers = await authHeaders();
  return apiGet<OperationHistory[]>(
    `${offeringPath(eventId, offeringId)}/appointment/undo-stack`,
    headers,
    true,
  );
}

export async function executeUndo(
  eventId: string,
  offeringId: string,
  operationId: string,
): Promise<void> {
  const headers = await authHeaders();
  await apiPost<unknown>(
    `${offeringPath(eventId, offeringId)}/appointment/undo/${operationId}`,
    {},
    headers,
    true,
  );
}

// --- Access Token (for WebSocket) ---

export async function getWsToken(): Promise<string> {
  // Access token lives in an HttpOnly cookie; JS cannot read it. The
  // WebSocket relies on the cookie riding along with the upgrade.
  return "";
}

export async function getGuestCompanions(
  eventId: string,
  guestId: string,
): Promise<import("@/types/Guest").Guest[]> {
  // Slotting-scoped companions lookup (cap slotting:read), authz-aware.
  const headers = await authHeaders();
  try {
    return await apiGet<import("@/types/Guest").Guest[]>(
      `${BASE}/${eventId}/guest/${guestId}/companions`,
      headers,
      true,
    );
  } catch (e) {
    console.error('getGuestCompanions failed:', e);
    return [];
  }
}

// --- Scheduling ---

function schedulingPath(eventId: string, offeringId: string) {
  return `${offeringPath(eventId, offeringId)}/scheduling`;
}

export async function createRecurringSeries(
  eventId: string,
  offeringId: string,
  pattern: RecurringPattern,
  templateAppointment: Partial<Appointment>,
): Promise<Appointment[]> {
  const headers = await authHeaders();
  return apiPost<Appointment[]>(
    `${schedulingPath(eventId, offeringId)}/recurring`,
    { pattern, templateAppointment },
    headers,
    true,
  );
}

export async function editRecurringInstance(
  eventId: string,
  offeringId: string,
  patternId: string,
  appointmentId: string,
  appointment: Partial<Appointment>,
  editMode: "THIS_INSTANCE" | "ALL_FUTURE",
): Promise<Appointment> {
  const headers = await authHeaders();
  return apiPut<Appointment>(
    `${schedulingPath(eventId, offeringId)}/recurring/${patternId}/appointment/${appointmentId}`,
    { appointment, editMode },
    headers,
    true,
  );
}

export async function deleteRecurringSeries(
  eventId: string,
  offeringId: string,
  patternId: string,
): Promise<void> {
  const headers = await authHeaders();
  await apiDelete<string>(
    `${schedulingPath(eventId, offeringId)}/recurring/${patternId}`,
    headers,
  );
}

export async function runAutoSchedule(
  eventId: string,
  offeringId: string,
  options: { respectAvailability: boolean; dryRun: boolean; sortRules?: { field: string; direction: string; optionOrder?: string[] }[] },
): Promise<BatchJob> {
  const headers = await authHeaders();
  return apiPost<BatchJob>(
    `${schedulingPath(eventId, offeringId)}/auto-schedule`,
    options,
    headers,
    true,
  );
}

export async function commitAutoSchedule(
  eventId: string,
  offeringId: string,
  batchJobId: string,
  excludedRequestIds?: string[],
): Promise<BatchJob> {
  const headers = await authHeaders();
  return apiPost<BatchJob>(
    `${schedulingPath(eventId, offeringId)}/auto-schedule/${batchJobId}/commit`,
    { excludedRequestIds: excludedRequestIds ?? [] },
    headers,
    true,
  );
}

/**
 * The device editor's field list.
 *
 * ⚠ `formula` is load-bearing in the projection, not decoration. `fields=` is a
 * REAL server-side projection and `InputValidator.sanitizeFields` drops what is
 * not requested — silently, with no 400 and no warning. Without it every field
 * arrives with `formula: undefined`, `isComputedField()` answers `false` for
 * all of them, and a COMPUTED column is offered as an `UPDATE_FIELD` target:
 * the action saves, and then every scan against it fails with
 * `computedFieldNotWritable`. The cockpit's other computed-aware fetch
 * (`ViewFilterHandling`) already asks for it, and says why in its own comment.
 */
export type DeviceAdditionalFieldRow = {
  fieldId: string;
  name: string;
  translation?: string;
  dataType: string;
  options?: string[];
  formula?: Record<string, unknown>;
};

export async function fetchAdditionalFields(
  eventId: string,
): Promise<DeviceAdditionalFieldRow[]> {
  const headers = await authHeaders();
  try {
    return await apiGet<DeviceAdditionalFieldRow[]>(
      `/api/v1/additional-fields/${eventId}?fields=fieldId,name,translation,dataType,options,formula`,
      headers,
      true,
    );
  } catch (e) {
    console.error('fetchAdditionalFields failed:', e);
    return [];
  }
}

export async function bulkConfirm(
  eventId: string,
  offeringId: string,
  appointmentIds: string[],
): Promise<BatchJob> {
  const headers = await authHeaders();
  return apiPost<BatchJob>(
    `${schedulingPath(eventId, offeringId)}/bulk-confirm`,
    { appointmentIds },
    headers,
    true,
  );
}

export async function bulkCancel(
  eventId: string,
  offeringId: string,
  appointmentIds: string[],
  reason: string,
): Promise<BatchJob> {
  const headers = await authHeaders();
  return apiPost<BatchJob>(
    `${schedulingPath(eventId, offeringId)}/bulk-cancel`,
    { appointmentIds, reason },
    headers,
    true,
  );
}

export async function bulkAssign(
  eventId: string,
  offeringId: string,
  assignments: { requestId: string; laneId: string; startDt: string; endDt: string; treatmentId?: string }[],
): Promise<BatchJob> {
  const headers = await authHeaders();
  return apiPost<BatchJob>(
    `${schedulingPath(eventId, offeringId)}/bulk-assign`,
    { assignments },
    headers,
    true,
  );
}

export async function getBatchJob(
  eventId: string,
  offeringId: string,
  batchJobId: string,
): Promise<BatchJob> {
  const headers = await authHeaders();
  return apiGet<BatchJob>(
    `${schedulingPath(eventId, offeringId)}/batch-job/${batchJobId}`,
    headers,
    true,
  );
}

// --- Stats ---

export async function getStats(
  eventId: string,
  offeringId: string,
  startDt: string,
  endDt: string,
): Promise<OfferingStats> {
  const headers = await authHeaders();
  return apiGet<OfferingStats>(
    `${offeringPath(eventId, offeringId)}/stats?startDt=${encodeURIComponent(startDt)}&endDt=${encodeURIComponent(endDt)}`,
    headers,
    true,
  );
}

export async function getDailyStats(
  eventId: string,
  offeringId: string,
  startDt: string,
  endDt: string,
): Promise<DailyBreakdown> {
  const headers = await authHeaders();
  return apiGet<DailyBreakdown>(
    `${offeringPath(eventId, offeringId)}/stats/daily?startDt=${encodeURIComponent(startDt)}&endDt=${encodeURIComponent(endDt)}`,
    headers,
    true,
  );
}

// --- Guest Appointments ---

export async function getGuestAppointments(
  eventId: string,
  offeringId: string,
  guestId: string,
): Promise<GuestAppointmentsResponse> {
  const headers = await authHeaders();
  return apiGet<GuestAppointmentsResponse>(
    `${offeringPath(eventId, offeringId)}/guest/${guestId}/appointments`,
    headers,
    true,
  );
}

export async function getGuestRequestsAllOfferings(
  eventId: string,
  guestId: string,
): Promise<AppointmentRequest[]> {
  const headers = await authHeaders();
  return apiGet<AppointmentRequest[]>(
    `${BASE}/${eventId}/guest/${guestId}/requests`,
    headers,
    true,
  );
}

export async function getGuestAppointmentsAllOfferings(
  eventId: string,
  guestId: string,
): Promise<GuestAppointmentsResponse> {
  const headers = await authHeaders();
  return apiGet<GuestAppointmentsResponse>(
    `${BASE}/${eventId}/guest/${guestId}/appointments`,
    headers,
    true,
  );
}

/**
 * RAW per-appointment rows for the email/document data-table PREVIEW, across all
 * offerings. Returns the recipient's actual appointments (incl. CANCELLED — the
 * marker expander applies the per-table status filter) so the preview matches
 * the inbox instead of illustrative SAMPLE rows. Operator-authed slotting:read.
 */
export async function getGuestAppointmentsPreview(
  eventId: string,
  guestId: string,
): Promise<AppointmentPreviewRow[]> {
  const headers = await authHeaders();
  return apiGet<AppointmentPreviewRow[]>(
    `${BASE}/${eventId}/guest/${guestId}/appointments-preview`,
    headers,
    true,
  );
}

export async function suggestSlots(
  eventId: string,
  offeringId: string,
  treatmentId: string | null,
  guestId: string | null,
  preferredDates: string[],
  count: number,
): Promise<SlotSuggestion[]> {
  const headers = await authHeaders();
  return apiPost<SlotSuggestion[]>(
    `${schedulingPath(eventId, offeringId)}/suggest-slots`,
    { treatmentId, guestId, preferredDates, count },
    headers,
    true,
  );
}

// --- Templates ---

export async function saveAsTemplate(
  eventId: string,
  offeringId: string,
  name: string,
  description: string,
): Promise<OfferingTemplate> {
  const headers = await authHeaders();
  return apiPost<OfferingTemplate>(
    `${offeringPath(eventId, offeringId)}/save-as-template`,
    { name, description },
    headers,
    true,
  );
}

// The library as seen FROM the event: the EVENT's company's templates, which
// is where save-as-template writes and where from-template looks a template
// up. The company route (`GET /slotting/templates`) lists the CALLER's
// company's library — for a collaborator from another company that was their
// own list, and every apply answered "Template not found".
export async function getTemplates(eventId: string): Promise<OfferingTemplate[]> {
  const headers = await authHeaders();
  return apiGet<OfferingTemplate[]>(
    `${BASE}/${eventId}/offering/templates`,
    headers,
    true,
  );
}

export async function createFromTemplate(
  eventId: string,
  templateId: string,
  overrides: { name: string; startDt: string; endDt: string; timezone?: string },
): Promise<Offering> {
  const headers = await authHeaders();
  return apiPost<Offering>(
    `${BASE}/${eventId}/offering/from-template/${templateId}`,
    overrides,
    headers,
    true,
  );
}

export async function updateTemplate(
  templateId: string,
  updates: Partial<OfferingTemplate>,
): Promise<OfferingTemplate> {
  const headers = await authHeaders();
  return apiPut<OfferingTemplate>(
    `${BASE}/templates/${templateId}`,
    updates,
    headers,
    true,
  );
}

export async function deleteTemplate(templateId: string): Promise<void> {
  const headers = await authHeaders();
  await apiDelete<string>(`${BASE}/templates/${templateId}`, headers);
}

// --- Ticket Rules ---

export async function getTicketRules(
  eventId: string,
  offeringId: string,
): Promise<OfferingTicketRule[]> {
  const headers = await authHeaders();
  return apiGet<OfferingTicketRule[]>(
    `${offeringPath(eventId, offeringId)}/ticket-rules`,
    headers,
    true,
  );
}

export async function upsertTicketRule(
  eventId: string,
  offeringId: string,
  rule: Partial<OfferingTicketRule>,
): Promise<OfferingTicketRule> {
  const headers = await authHeaders();
  return apiPost<OfferingTicketRule>(
    `${offeringPath(eventId, offeringId)}/ticket-rules`,
    rule,
    headers,
    true,
  );
}

export async function deleteTicketRule(
  eventId: string,
  offeringId: string,
  ruleId: string,
): Promise<void> {
  const headers = await authHeaders();
  await apiDelete<string>(
    `${offeringPath(eventId, offeringId)}/ticket-rules/${ruleId}`,
    headers,
  );
}

// --- Export/Import ---

export async function exportCsv(
  eventId: string,
  offeringId: string,
  startDt: string,
  endDt: string,
): Promise<void> {
  const headers = await authHeaders();
  await apiGetRaw(
    `${offeringPath(eventId, offeringId)}/export/csv?startDt=${encodeURIComponent(startDt)}&endDt=${encodeURIComponent(endDt)}`,
    headers,
  );
}

export async function getExportCsvUrl(
  eventId: string,
  offeringId: string,
  startDt: string,
  endDt: string,
): Promise<string> {
  // Return the full URL for client-side download
  return `${offeringPath(eventId, offeringId)}/export/csv?startDt=${encodeURIComponent(startDt)}&endDt=${encodeURIComponent(endDt)}`;
}

export async function getExportExcelUrl(
  eventId: string,
  offeringId: string,
  startDt: string,
  endDt: string,
): Promise<string> {
  return `${offeringPath(eventId, offeringId)}/export/excel?startDt=${encodeURIComponent(startDt)}&endDt=${encodeURIComponent(endDt)}`;
}

export async function importCsv(
  eventId: string,
  offeringId: string,
  fileContent: string,
): Promise<BatchJob> {
  const headers = await authHeaders();
  const formData = new FormData();
  formData.append("file", new Blob([fileContent], { type: "text/csv" }), "import.csv");
  return apiPostFormData<BatchJob>(
    `${offeringPath(eventId, offeringId)}/export/import/csv`,
    formData,
    headers,
  );
}

// --- Check-in ---

export async function checkInGuest(
  eventId: string,
  offeringId: string,
  appointmentId: string,
  guestId: string,
): Promise<Appointment> {
  const headers = await authHeaders();
  return apiPost<Appointment>(
    `${offeringPath(eventId, offeringId)}/appointment/${appointmentId}/check-in`,
    { guestId },
    headers,
    true,
  );
}

// --- Waitlist ---

export async function getWaitlist(
  eventId: string,
  offeringId: string,
  appointmentId: string,
): Promise<WaitlistEntry[]> {
  const headers = await authHeaders();
  return apiGet<WaitlistEntry[]>(
    `${offeringPath(eventId, offeringId)}/appointment/${appointmentId}/waitlist`,
    headers,
    true,
  );
}

export async function joinWaitlist(
  eventId: string,
  offeringId: string,
  appointmentId: string,
  guestId: string,
): Promise<WaitlistEntry> {
  const headers = await authHeaders();
  return apiPost<WaitlistEntry>(
    `${offeringPath(eventId, offeringId)}/appointment/${appointmentId}/waitlist`,
    { guestId },
    headers,
    true,
  );
}

export async function cancelWaitlistEntry(
  eventId: string,
  offeringId: string,
  appointmentId: string,
  waitlistEntryId: string,
): Promise<void> {
  const headers = await authHeaders();
  await apiDelete<string>(
    `${offeringPath(eventId, offeringId)}/appointment/${appointmentId}/waitlist/cancel?waitlistEntryId=${encodeURIComponent(waitlistEntryId)}`,
    headers,
  );
}

export async function promoteFromWaitlist(
  eventId: string,
  offeringId: string,
  appointmentId: string,
  freedSpots: number = 1,
): Promise<WaitlistEntry[]> {
  const headers = await authHeaders();
  return apiPost<WaitlistEntry[]>(
    `${offeringPath(eventId, offeringId)}/appointment/${appointmentId}/waitlist/promote`,
    { freedSpots },
    headers,
    true,
  );
}

// --- Linked Groups ---

export async function createLinkedGroup(
  eventId: string,
  offeringId: string,
  linkedGroup: {
    guestId: string;
    name: string;
    appointmentIds: string[];
  },
): Promise<LinkedAppointmentGroup> {
  const headers = await authHeaders();
  return apiPost<LinkedAppointmentGroup>(
    `${offeringPath(eventId, offeringId)}/scheduling/linked-group`,
    linkedGroup,
    headers,
    true,
  );
}
